import { mappedColors, typography } from '@pixee/design-system'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Spinner } from '../../components/spinner'
import { Routes } from '../../routes'
import { useGetScanAnalysis } from '../../utils/api-client/api-hooks.ts'
import * as styles from './analysis-to-scan-redirect.css'

/**
 * Redirect component that fetches the analysis to get the scan ID, then redirects to the scan details page.
 * Used to support legacy /analysis/:analysisId routes by redirecting them to /scans/:scanId.
 */
export const AnalysisToScanRedirect: React.FC = () => {
  const { analysisId } = useParams<{ analysisId: string }>()
  const navigate = useNavigate()

  const {
    data: scanAnalysis,
    isLoading,
    isError,
  } = useGetScanAnalysis({
    analysisId,
  })

  const scanId = scanAnalysis?._embedded?.scan?.id
  // A loaded analysis with no embedded scan isn't expected, but the schema permits it; surface as an error
  // so the user sees the same "doesn't exist" UI instead of a blank page.
  const hasMissingScan = !isLoading && scanAnalysis !== undefined && scanId === undefined

  useEffect(() => {
    if (scanId !== undefined) {
      navigate(Routes.ScanDetailsPage.createPath({ scanId }), { replace: true })
    }
  }, [scanId, navigate])

  return <AnalysisToScanRedirectPure isLoading={isLoading} isError={isError || hasMissingScan} />
}

export type AnalysisToScanRedirectPureProps = {
  isLoading: boolean
  isError: boolean
}

export const AnalysisToScanRedirectPure: React.FC<AnalysisToScanRedirectPureProps> = ({ isLoading, isError }) => {
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: '16px',
        }}
      >
        <Spinner size="64" label="Redirecting to scan details..." />
        <span className={typography.paragraph} style={{ color: mappedColors.neutral.neutral10 }}>
          This link format is outdated. Redirecting you to the new location...
        </span>
      </div>
    )
  }

  if (isError) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: '24px',
          gap: '16px',
        }}
      >
        <span className={typography.paragraphItalic} style={{ color: mappedColors.neutral.neutral10 }}>
          The analysis you're looking for doesn't exist or may have been removed.
        </span>
        <a href={Routes.RepositoriesPage.path} className={styles.homeLink}>
          Go to repositories
        </a>
      </div>
    )
  }

  return null
}
